<!-- ======================================================================== -->
<!-- TOOLBAR                                                                 -->
<!-- ======================================================================== -->
@if (!showEmptyState || context || pageContext) {
  <div class="notes-list__toolbar">
    <!-- Navigation par onglets -->
    <div class="notes-list__header">
      <app-eva-tabs-navigation
        [tabs]="[
          { label: 'Toutes mes notes', value: 'all', icon: 'pi-file-edit' },
          { label: 'Épinglées', value: 'pinned', icon: 'pi-thumbtack' },
        ]"
        [activeTab]="activeTab"
        (tabChange)="onTabChange($event)"
      ></app-eva-tabs-navigation>
    </div>

    <!-- Context filter badge -->
    @if (context) {
      <div class="notes-list__context-badge">
        <span class="notes-list__context-label">
          <i class="pi pi-filter"></i>
          {{ context.objectLabel }}
        </span>
        <eva-button
          icon="heroXMark"
          variant="text"
          size="small"
          (onClick)="removeContextFilter()"
          ariaLabel="Retirer le filtre"
        ></eva-button>
      </div>
    }

    <!-- Filtres chips par type d'objet lié -->
    @if (!context) {
      <div class="notes-list__filter-chips">
        <!-- Retour au filtre sur l'objet de la page, comme dans les tâches -->
        @if (pageContext) {
          <button
            type="button"
            class="notes-list__filter-chip notes-list__filter-chip--context"
            [evaTooltip]="'Revenir aux notes de ' + pageContext.objectLabel"
            evaTooltipPosition="bottom"
            (click)="restoreContextFilter()"
          >
            <i class="pi pi-filter"></i>
            {{ pageContext.objectLabel }}
          </button>
          <span class="notes-list__chips-divider" aria-hidden="true"></span>
        }
        @for (chip of visibleNoteTypeChips; track chip.type) {
          <button
            type="button"
            class="notes-list__filter-chip"
            [class.notes-list__filter-chip--active]="activeTypeFilter === chip.type"
            (click)="setTypeFilter(chip.type)"
          >
            <ng-icon [name]="chip.icon"></ng-icon>
            {{ chip.label }}
            @if (chip.type !== "ALL") {
              <span class="notes-list__filter-count">{{ noteTypeCounts[chip.type] ?? 0 }}</span>
            }
          </button>
        }
      </div>
    }

    <!-- Barre de recherche + tri + création -->
    <div class="notes-list__search-sort-row">
      <div class="search-bar">
        <eva-input
          icon="heroMagnifyingGlass"
          [clearable]="true"
          placeholder="Rechercher une note..."
          size="inline"
          [value]="searchTerm"
          (valueChange)="onSearchInput($event)"
        >
        </eva-input>
      </div>
      <div class="notes-list__sort">
        <eva-button
          icon="heroArrowsUpDown"
          variant="text"
          size="small"
          [severity]="showSortMenu || sortBy !== 'date' ? 'primary' : 'secondary'"
          (onClick)="$event.stopPropagation(); toggleSortMenu()"
          [evaTooltip]="'Trier : ' + sortLabel"
          evaTooltipPosition="left"
        ></eva-button>
        @if (showSortMenu) {
          <div class="notes-list__sort-menu">
            <button
              type="button"
              class="notes-list__sort-option"
              [class.notes-list__sort-option--active]="sortBy === 'date'"
              (click)="setSortBy('date')"
            >
              <i class="pi pi-calendar"></i> Date
            </button>
            <button
              type="button"
              class="notes-list__sort-option"
              [class.notes-list__sort-option--active]="sortBy === 'title'"
              (click)="setSortBy('title')"
            >
              <i class="pi pi-sort-alpha-down"></i> Titre A→Z
            </button>
          </div>
        }
      </div>
      <eva-button icon="heroPlus" label="Note" severity="primary" size="small" (onClick)="onCreateNote()"></eva-button>
    </div>
  </div>
}

<!-- ======================================================================== -->
<!-- LOADING SKELETON                                                         -->
<!-- ======================================================================== -->
@if (isLoading && notes.length === 0) {
  <div class="notes-list__skeleton">
    @for (i of [1, 2, 3]; track i) {
      <div class="notes-list__skeleton-card">
        <div class="notes-list__skeleton-title"></div>
        <div class="notes-list__skeleton-content"></div>
        <div class="notes-list__skeleton-meta"></div>
      </div>
    }
  </div>
}

<!-- ======================================================================== -->
<!-- NOTES LIST                                                               -->
<!-- ======================================================================== -->
@if (!showEmptyState && !showNoResults && !(isLoading && notes.length === 0)) {
  <div class="notes-list__scroll" (scroll)="onScroll($event)" #scrollContainer>
    <!-- Section "Sur cette page" -->
    @if (showPageSection) {
      <div class="notes-section notes-section--page">
        <div class="notes-section__page-header">
          <eva-tag [preset]="pageContextPreset" size="small" [rounded]="true"></eva-tag>
        </div>
        @for (note of pageContextNotes; track note.note_id) {
          <div class="note-card" (click)="openNote(note.note_id)" (contextmenu)="onNoteRightClick($event, note)">
            <div class="note-card-header">
              <div class="note-card-header__left">
                @if (note.is_pinned) {
                  <i class="pi pi-thumbtack note-pin-icon"></i>
                }
                <div class="note-card-title">{{ note.title || "Sans titre" }}</div>
              </div>
              <div class="note-card-header__right">
                @if (visibleAttachments(note, pageContext?.objectId).length) {
                  <eva-tag
                    [preset]="getAttachmentPreset(visibleAttachments(note, pageContext?.objectId)[0])"
                    size="small"
                    [rounded]="true"
                  ></eva-tag>
                  @if (visibleAttachments(note, pageContext?.objectId).length > 1) {
                    <eva-tag
                      [label]="
                        '+' +
                        (visibleAttachments(note, pageContext?.objectId).length - 1) +
                        ' autre' +
                        (visibleAttachments(note, pageContext?.objectId).length > 2 ? 's' : '')
                      "
                      severity="secondary"
                      size="small"
                      [rounded]="true"
                      [evaTooltip]="getOverflowTooltip(visibleAttachments(note, pageContext?.objectId))"
                      evaTooltipPosition="top"
                    ></eva-tag>
                  }
                }
                <span class="note-card-date">{{ formatRelativeDate(note.updated_on) }}</span>
              </div>
            </div>
            @if (note.content) {
              <div class="note-card-preview">{{ stripMarkdown(note.content) }}</div>
            }
          </div>
        }
      </div>
    }

    <!-- Pinned section -->
    @if (pinnedNotes.length > 0 && activeTab === "all") {
      <div class="notes-section">
        <div class="notes-section-tag-header">
          <eva-tag [preset]="pinnedSectionPreset" size="small" [rounded]="true"></eva-tag>
        </div>

        @for (note of pinnedNotes; track note.note_id) {
          <div class="note-card" (click)="openNote(note.note_id)" (contextmenu)="onNoteRightClick($event, note)">
            <div class="note-card-header">
              <div class="note-card-header__left">
                <i class="pi pi-thumbtack note-pin-icon"></i>
                <div class="note-card-title">{{ note.title || "Sans titre" }}</div>
              </div>
              <div class="note-card-header__right">
                @if (visibleAttachments(note, context?.objectId).length) {
                  <eva-tag
                    [preset]="getAttachmentPreset(visibleAttachments(note, context?.objectId)[0])"
                    size="small"
                    [rounded]="true"
                  ></eva-tag>
                  @if (visibleAttachments(note, context?.objectId).length > 1) {
                    <eva-tag
                      [label]="
                        '+' +
                        (visibleAttachments(note, context?.objectId).length - 1) +
                        ' autre' +
                        (visibleAttachments(note, context?.objectId).length > 2 ? 's' : '')
                      "
                      severity="secondary"
                      size="small"
                      [rounded]="true"
                      [evaTooltip]="getOverflowTooltip(visibleAttachments(note, context?.objectId))"
                      evaTooltipPosition="top"
                    ></eva-tag>
                  }
                }
                <span class="note-card-date">{{ formatRelativeDate(note.updated_on) }}</span>
              </div>
            </div>
            @if (note.content) {
              <div class="note-card-preview">{{ stripMarkdown(note.content) }}</div>
            }
          </div>
        }
      </div>
    }

    <!-- Section "Autres" : non-épinglées, non concernées par le contexte de page -->
    @if (otherNotes.length > 0 && activeTab === "all") {
      <div class="notes-section">
        @if (pinnedNotes.length > 0 || showPageSection) {
          <div class="notes-section-tag-header">
            <eva-tag [preset]="recentSectionPreset" size="small" [rounded]="true"></eva-tag>
          </div>
        }

        @for (note of otherNotes; track note.note_id) {
          <div class="note-card" (click)="openNote(note.note_id)" (contextmenu)="onNoteRightClick($event, note)">
            <div class="note-card-header">
              <div class="note-card-header__left">
                <div class="note-card-title">{{ note.title || "Sans titre" }}</div>
              </div>
              <div class="note-card-header__right">
                @if (visibleAttachments(note, context?.objectId).length) {
                  <eva-tag
                    [preset]="getAttachmentPreset(visibleAttachments(note, context?.objectId)[0])"
                    size="small"
                    [rounded]="true"
                  ></eva-tag>
                  @if (visibleAttachments(note, context?.objectId).length > 1) {
                    <eva-tag
                      [label]="
                        '+' +
                        (visibleAttachments(note, context?.objectId).length - 1) +
                        ' autre' +
                        (visibleAttachments(note, context?.objectId).length > 2 ? 's' : '')
                      "
                      severity="secondary"
                      size="small"
                      [rounded]="true"
                      [evaTooltip]="getOverflowTooltip(visibleAttachments(note, context?.objectId))"
                      evaTooltipPosition="top"
                    ></eva-tag>
                  }
                }
                <span class="note-card-date">{{ formatRelativeDate(note.updated_on) }}</span>
              </div>
            </div>
            @if (note.content) {
              <div class="note-card-preview">{{ stripMarkdown(note.content) }}</div>
            }
          </div>
        }
      </div>
    }

    <!-- Pinned only view (for "pinned" tab) -->
    @if (activeTab === "pinned") {
      <div class="notes-section">
        @for (note of pinnedNotes; track note.note_id) {
          <div class="note-card" (click)="openNote(note.note_id)" (contextmenu)="onNoteRightClick($event, note)">
            <div class="note-card-header">
              <div class="note-card-header__left">
                <i class="pi pi-thumbtack note-pin-icon"></i>
                <div class="note-card-title">{{ note.title || "Sans titre" }}</div>
              </div>
              <div class="note-card-header__right">
                @if (visibleAttachments(note, context?.objectId).length) {
                  <eva-tag
                    [preset]="getAttachmentPreset(visibleAttachments(note, context?.objectId)[0])"
                    size="small"
                    [rounded]="true"
                  ></eva-tag>
                  @if (visibleAttachments(note, context?.objectId).length > 1) {
                    <eva-tag
                      [label]="
                        '+' +
                        (visibleAttachments(note, context?.objectId).length - 1) +
                        ' autre' +
                        (visibleAttachments(note, context?.objectId).length > 2 ? 's' : '')
                      "
                      severity="secondary"
                      size="small"
                      [rounded]="true"
                      [evaTooltip]="getOverflowTooltip(visibleAttachments(note, context?.objectId))"
                      evaTooltipPosition="top"
                    ></eva-tag>
                  }
                }
                <span class="note-card-date">{{ formatRelativeDate(note.updated_on) }}</span>
              </div>
            </div>
            @if (note.content) {
              <div class="note-card-preview">{{ stripMarkdown(note.content) }}</div>
            }
          </div>
        }
      </div>
    }

    <!-- Loading more spinner -->
    @if (isLoading && notes.length > 0) {
      <div class="notes-list__load-more">
        <i class="pi pi-spinner pi-spin"></i>
        <span>Chargement...</span>
      </div>
    }
  </div>
}

<!-- ======================================================================== -->
<!-- NO RESULTS (search active, no matches)                                   -->
<!-- ======================================================================== -->
@if (showNoResults) {
  <div class="notes-list__no-results">
    <i class="pi pi-search"></i>
    <p>Aucune note trouvée pour "{{ searchTerm }}"</p>
  </div>
}

<!-- ======================================================================== -->
<!-- CONTEXT MENU (clic droit)                                               -->
<!-- ======================================================================== -->
@if (contextMenu.visible && contextMenu.note) {
  <div
    class="notes-list__context-menu"
    [style.top.px]="contextMenu.y"
    [style.left.px]="contextMenu.x"
    (click)="$event.stopPropagation()"
  >
    <button type="button" class="notes-list__context-item" (click)="contextMenuOpen(contextMenu.note!)">
      <i class="pi pi-file-edit"></i>
      <span>Ouvrir</span>
    </button>
    <button type="button" class="notes-list__context-item" (click)="contextMenuTogglePin(contextMenu.note!)">
      <i class="pi pi-thumbtack"></i>
      <span>{{ contextMenu.note!.is_pinned ? "Désépingler" : "Épingler" }}</span>
    </button>
    <div class="notes-list__context-separator"></div>
    <button
      type="button"
      class="notes-list__context-item notes-list__context-item--danger"
      (click)="contextMenuDelete(contextMenu.note!)"
    >
      <i class="pi pi-trash"></i>
      <span>Supprimer</span>
    </button>
  </div>
}

<eva-dialog
  [(visible)]="showDeleteDialog"
  header="Supprimer cette note ?"
  type="confirm"
  confirmLabel="Supprimer"
  cancelLabel="Annuler"
  confirmSeverity="danger"
  size="sm"
  (onConfirm)="confirmDelete()"
  (onCancel)="showDeleteDialog = false"
>
  <p>La note sera archivée et pourra être restaurée par un administrateur.</p>
</eva-dialog>

<!-- ======================================================================== -->
<!-- EMPTY STATE (no notes at all)                                            -->
<!-- ======================================================================== -->
@if (showEmptyState) {
  <div class="notes-todos-empty">
    <div class="notes-todos-empty__icon notes-todos-empty__icon--info">
      <i class="pi pi-file-edit"></i>
    </div>
    @if (context) {
      <h4 class="notes-todos-empty__title">Aucune note pour {{ context.objectLabel }}</h4>
      <p class="notes-todos-empty__description">Créez une note rattachée ou consultez toutes vos notes</p>
      <div class="notes-todos-empty__action">
        <eva-button
          label="Créer une note"
          icon="heroPlus"
          severity="primary"
          [block]="true"
          (onClick)="onCreateNote()"
        ></eva-button>
      </div>
      <div class="notes-todos-empty__action" style="margin-top: 8px">
        <eva-button
          label="Voir toutes mes notes"
          icon="heroListBullet"
          severity="secondary"
          [block]="true"
          [text]="true"
          (onClick)="removeContextFilter()"
        ></eva-button>
      </div>
    } @else if (pageContext) {
      <!-- Filtre retiré sur une fiche : l'en-tête reste affiché, état vide court -->
      <h4 class="notes-todos-empty__title">Aucune note</h4>
      <p class="notes-todos-empty__description">Capturez vos idées et informations importantes en un clic</p>
      <div class="notes-todos-empty__action">
        <eva-button
          label="Créer une note"
          icon="heroPlus"
          severity="primary"
          [block]="true"
          (onClick)="onCreateNote()"
        ></eva-button>
      </div>
    } @else {
      <h4 class="notes-todos-empty__title">Aucune note enregistrée</h4>
      <p class="notes-todos-empty__description">Capturez vos idées et informations importantes en un clic</p>
      <div class="notes-todos-empty__action">
        <eva-button
          label="Créer une note"
          icon="heroPlus"
          severity="primary"
          [block]="true"
          (onClick)="onCreateNote()"
        ></eva-button>
      </div>
      <p class="notes-todos-empty__hint">
        <i class="pi pi-info-circle"></i>
        Astuce : les notes vous permettront de garder vos informations à portée de main
      </p>
    }
  </div>
}
